import { memo, useEffect, useRef, useState } from "react";
import { ECharts } from "echarts";
import * as echarts from "echarts";
import moment from "moment";

import DeviceMetricApi from "../../../../api/DeviceMetricApi";
import StringUtils from "../../../../util/StringUtils";
import TimeUtils from "../../../../util/TimeUtils";
import SeriesColorUtils from "../../../../util/SeriesColorUtils";
import { ChartHeader, ChartUnit, ChartName, ChartWrapper, DeviceMetricsWrapper, EChartsWrapper, ChartCurrentValue, ChartLatestMetricTime, ChartInfo, ChartHeaderColorBlock } from "./RealtimeDeviceMetrics.styled";

interface DeviceMetricProps {
    deviceView: any
}

const RealtimeDeviceMetrics: React.FC<DeviceMetricProps> = (props)=> {
    // params
    let {deviceView} = props;

    // api
    let deviceMetricApi = new DeviceMetricApi();

    // ref
    let echartsDomsRef = useRef<HTMLElement[]>([]);
    let echartsRef =useRef<ECharts[]>([]);
    let deviceMetricsSeriesListRef = useRef([]);

    // state
    let [deviceMetricProperties, setDeviceMetricProperties] = useState([]);
    let [repaint, setRepaint] = useState(null);

    // functions
    let fetchRealtimeMetricsSeries = async ()=> {
        // fetch data
        let deviceMetricsSeriesList = (await deviceMetricApi.getRealtimeDeviceMetricsSeries(deviceView.device.id)).data.data;
        return deviceMetricsSeriesList;
    }

    let updateSeriesData = async (deviceMetricsSeriesList: any[])=> {
        let seriesDatas = [];

        // process
        for(let i=0; i<deviceMetricsSeriesList.length; i++) {
            let deviceMetricsSeries = deviceMetricsSeriesList[i];
            let property = deviceMetricProperties[i];
            
            // series data
            let seriesData = [];
            let metrics = deviceMetricsSeries.metrics;
            let lastMetricTimestamp: number = null;
            let paddingGapMillis = 5 * 10000;
            for (let i = 0; i < metrics.length; i++) {
                let metric = metrics[i];
                
                // filter error metrics (mainly caused by sensor errors)
                let errorMetric = false;
                if (property.min != null && Number(metric.value) < property.min) {
                    errorMetric = true;
                } else if (property.max != null && Number(metric.value) > property.max) {
                    errorMetric = true;
                }
                if (errorMetric) {
                    continue;
                }
                
                // pad data point
                if (lastMetricTimestamp !=null && Math.abs(metric.timestamp - lastMetricTimestamp) > paddingGapMillis) {
                    // down data point
                    // let downDataPoint = {name: '' + (Number(lastMetricTimestamp)+1), value: [(Number(lastMetricTimestamp)+1), null]};
                    // seriesData.push(downDataPoint);

                    // up data point
                    // let upDataPoint: any = {name: '' + (Number(metric.timestamp)-1), value: [(Number(metric.timestamp)-1), null]};
                    // seriesData.push(upDataPoint);
                    seriesData.push(null);
                }
                lastMetricTimestamp = metric.timestamp;

                // add to series data
                seriesData.push({
                    name: metric.timestamp + '',
                    value: [Number(metric.timestamp), Number(metric.value)],
                });
            }

            // seriesDatas
            seriesDatas.push(seriesData);
        }

        // min/max timestam
        let minTime: number;
        let maxTime: number;
        for(let i=0; i<seriesDatas.length; i++) {
            let seriesData = seriesDatas[i];
            if (seriesData.length <= 0) {
                continue;
            }
            let minTimestamp = seriesData[0].value[0];
            let maxTimestamp = seriesData[seriesData.length-1].value[0];
            minTime = minTime == null ? minTimestamp : Math.min(minTime, minTimestamp);
            maxTime = maxTime == null ? maxTimestamp : Math.max(maxTime, maxTimestamp);
        }

        for(let i=0; i<seriesDatas.length; i++) {
            let seriesData = seriesDatas[i];
            // echarts options
            if (echartsRef.current[i] != null) {
                echartsRef.current[i].setOption({
                    xAxis: {
                        min: minTime,
                        max: maxTime,
                    },
                    series: [{
                        data: seriesData
                    }]
                });
            }
        }
    };

    let formatTimestampAxis = (value)=> {
        let text = moment(value).format('YYYY/MM/DD HH:mm:ss');
        text=text.replace(' ', '\n');
        return text;
    }
    
    // init ECharts
    let initECharts = (dom: HTMLElement, index: number)=> {
        if (dom == null) {
            // element unmount event
            let echarts = echartsRef.current[index];
            echarts?.dispose();
            echartsRef.current[index] = null;
            return;
        }
        if (echarts.getInstanceByDom(dom)) {
            return;
        }

        // save to reference
        echartsDomsRef.current[index]=dom;
        let seriesIndex = echartsDomsRef.current.indexOf(dom);

        // init echarts
        let chart = echarts.init(dom);
        echartsRef.current[index]=chart;

        // series
        let property = deviceMetricProperties[seriesIndex];

        // init options
        let option = {
            backgroundColor: 'transparent',
            tooltip: {
                trigger: 'axis',
                axisPointer: {
                    animation: false
                },
                confine: true,
                backgroundColor: "rgba(0,0,0,0.7)",
                formatter: function(params) {
                    if (params == null) {
                        return '';
                    }

                    let seriesContent = '';
                    for(let param of params) {
                        if (param.value==null) {
                            continue;
                        }
                        
                        let errorText = '';
                        if (property.min != null && Number(param.value[1]) < property.min) {
                            errorText = '（故障）';
                        } else if (property.max != null && Number(param.value[1]) > property.max) {
                            errorText = '（故障）';
                        }
                        
                        seriesContent += `
                            <div style="color: #EEEEEE;">
                                <div style="margin: 0px 0px 5px 0px;">
                                    <div style="display: inline-block; width: 10px; height: 10px; border-radius: 50%; margin-right: 5px; background-color: ${param.color}"></div>
                                    <div style="display: inline-block">${param.seriesName}</div>
                                </div>
                                <div style="padding: 5px 0px;">
                                    <span style="display: inline-block; width: 50px;">测量值:</span> 
                                    <span style="display: inline-block">${param.value[1]} ${StringUtils.nonull(property.unit)} ${errorText}</span>
                                </div>
                                <div>
                                    <span style="display: inline-block; width: 50px;">时间:</span> 
                                    <span style="display: inline-block">${moment(param.value[0]).format('YYYY-MM-DD HH:mm:ss')}</span>
                                </div>
                            </div>
                        `;
                        break;
                    }
                    let content=`
                        <div style="width: 220px;">
                            ${seriesContent}
                        </div>
                    `;
                    return content;
                },
            },
            grid: {
                x: 50,
                y: 20,
                x2: 20,
                y2: 40,
            },
            xAxis: {
                type: 'time',
                minorTick: {
                    show: true
                },
                minorSplitLine: {
                    show: true,
                    lineStyle: {
                        color: 'rgba(255, 255, 255, 0.1)',
                        width: 1,
                        type: 'solid',
                    },
                },
                splitLine: {
                    show: true,
                    lineStyle: {
                        color: 'rgba(255, 255, 255, 0.2)',
                        width: 1,
                        type: 'solid',
                    },
                },
                splitNumber: 2,
                axisLabel: {
                    formatter: function (value, index) {
                        return formatTimestampAxis(value);
                    },
                    color: 'rgba(255, 255, 255, 0.5)'
                },
            },
            yAxis: {
                type: 'value',
                boundaryGap: [0, '100%'],
                axisLine: {
                    show: true
                },
                minorTick: {
                    show: true
                },
                minorSplitLine: {
                    show: true,
                    lineStyle: {
                        color: 'rgba(255, 255, 255, 0.1)',
                        width: 1,
                        type: 'solid',
                    },
                },
                splitLine: {
                    show: true,
                    lineStyle: {
                        color: 'rgba(255, 255, 255, 0.2)',
                        width: 1,
                        type: 'solid',
                    },
                },
                nameTextStyle: {
                    align: 'left',
                },
                axisLabel: { color: 'rgba(255, 255, 255, 0.5)' },
                min: property.min != null ? parseFloat(property.min) : 'dataMin',
                max: property.max != null ? parseFloat(property.max) : 'dataMax',
            },
            dataZoom: [
                {
                  type: 'inside',
                  start: 90,
                  end: 100
                }
            ],
            series: [
                {
                    name: property.name,
                    type: 'line',
                    color: SeriesColorUtils.getSeriesColor(seriesIndex),
                    smooth: false,
                    showSymbol: false,
                    areaStyle: {
                        opacity: 0.2,
                    },
                    data: [],
                    shadowBlur: 20,
                }
            ],
        };

        chart.setOption(option);
        chart.group='DeviceRealtimeMetrics';
        echarts.connect('DeviceRealtimeMetrics');

        if (deviceMetricProperties.length == seriesIndex + 1) {
            // fetch data
            setTimeout(()=> {
                updateSeriesData(deviceMetricsSeriesListRef.current);
            });
        }
    }

    // init ECharts reference
    let initEChartsRef = useRef(initECharts);

    // effect
    useEffect(()=> {
        let refreshTaskId: any = null;
        let disposed = false;

        let init = async ()=> {
            // fetch data
            let deviceMetricsSeriesList = await fetchRealtimeMetricsSeries();
            deviceMetricsSeriesListRef.current = deviceMetricsSeriesList;

            // device template properties
            if (deviceMetricProperties.length <=0 ) {
                deviceMetricProperties = [];
                for(let deviceMetricsSeries of deviceMetricsSeriesList) {
                    deviceMetricProperties.push(deviceMetricsSeries.property);
                }
                setDeviceMetricProperties(deviceMetricProperties);    
            } else {
                updateSeriesData(deviceMetricsSeriesList);
            }

            // refresh task
            refreshTaskId = window.setInterval(async ()=> {
                deviceMetricsSeriesList = await fetchRealtimeMetricsSeries();
                deviceMetricsSeriesListRef.current = deviceMetricsSeriesList;
                updateSeriesData(deviceMetricsSeriesList);
                setDeviceMetricProperties([].concat(deviceMetricProperties))

                if (disposed) {
                    window.clearInterval(refreshTaskId);
                }
            }, 7500);
        };

        init();

        // resize charts on window resize
        let resizeListener = ()=> {
            for(let chart of echartsRef.current) {
                chart?.resize();
            }
        };
        window.addEventListener('resize', resizeListener);

        // unload
        return ()=> {
            disposed = true;
            for(let chart of echartsRef.current) {
                chart?.dispose();
            }

            // clear interval
            if (refreshTaskId!=null) {
                window.clearInterval(refreshTaskId);
            }

            // clear resize listener
            window.removeEventListener('resize', resizeListener);
        }
    }, []);

    // render
    let getLatestMetricTime = (index: number)=> {
        let deviceMetricsSeries = deviceMetricsSeriesListRef.current[index];
        if (deviceMetricsSeries != 0) {
            let metrics = deviceMetricsSeries.metrics;
            if (metrics.length > 0) {
                let latestMetrics = metrics[metrics.length-1];
                let time = TimeUtils.formatLastTime(Number(latestMetrics.timestamp));
                return time;
            }
        }
        return '';
    };

    let getLatestMetricValue = (index: number)=> {
        let deviceMetricProperty = deviceMetricProperties[index];
        let deviceMetricsSeries = deviceMetricsSeriesListRef.current[index];
        if (deviceMetricsSeries != 0) {
            let metrics = deviceMetricsSeries.metrics;
            if (metrics.length > 0) {
                let latestMetrics = metrics[metrics.length-1];
                let errorText = '';
                if (deviceMetricProperty.min != null && Number(latestMetrics.value) < Number(deviceMetricProperty.min)
                || deviceMetricProperty.max != null && Number(latestMetrics.value) > Number(deviceMetricProperty.max)) {
                    // errorText = ' 故障';
                    return '故障';
                }
                let text = `${latestMetrics.value}${errorText}`;
                return text;
            }
        }
        return '';
    };

    return (
        <DeviceMetricsWrapper>
            {
                deviceMetricProperties.map((e, index)=> {
                    return (
                        <ChartWrapper key={e.name}>
                            <ChartHeader>
                                <ChartName>{e.name}</ChartName>
                                <ChartCurrentValue>{getLatestMetricValue(index)}</ChartCurrentValue>
                                <ChartInfo>
                                    <ChartLatestMetricTime>最后更新时间：{getLatestMetricTime(index)}</ChartLatestMetricTime>
                                    <ChartUnit>单位：{StringUtils.emptyToSlash(e.unit)}</ChartUnit>
                                </ChartInfo>
                            </ChartHeader>
                            <EChartsDom index={index} refCallback={initEChartsRef.current} />
                        </ChartWrapper>
                    )
                })
            }
        </DeviceMetricsWrapper>
    )
}

interface EChartsDomProps {
    index: number;
    refCallback: (dom: HTMLDivElement, index: number)=> void;
}

let EChartsDom: React.FC<EChartsDomProps> = memo((props)=> {
    return <EChartsWrapper ref={(dom)=> props.refCallback(dom, props.index)}>
    </EChartsWrapper>
});


export default RealtimeDeviceMetrics;
